import { useState, useEffect } from 'react';
import { getScannerLabel } from '../../contexts/ScannersContext';
import Button from "@mui/material/Button";
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import { useAuth } from '../../contexts/AuthContext';
import TruCard from '../TruCard';
import * as theme from '../../theme';
import { Phone } from '@mui/icons-material';

const WeekView = ({ weekStart, setWeekStart, scheduleData, loading, onEditDay }) => {
  const [windowWidth, setWindowWidth] = useState(() => {
    return typeof window !== 'undefined' ? window.innerWidth : 1200;
  });
  const { currentUser } = useAuth();

  // Update window width on resize
  useEffect(() => {
    const handleResize = () => {
      const newWidth = window.innerWidth;
      setWindowWidth(newWidth);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Determine number of days to display and navigation step
  const getDisplayConfig = () => {
    if (windowWidth < 600) {
      return { daysToShow: 2, navStep: 2 }; // Changed from 1 to 2
    } else if (windowWidth < 1200) {
      return { daysToShow: 3, navStep: 3 };
    } else {
      return { daysToShow: 7, navStep: 7 };
    }
  };

  const { daysToShow, navStep } = getDisplayConfig();
  // Log display configuration for debugging
  useEffect(() => {
  }, [windowWidth, daysToShow, navStep]);

  // Handle Previous/Next navigation
  const handlePreviousWeek = () => {
    const newWeekStart = weekStart.subtract(navStep, 'day');
    setWeekStart(newWeekStart);
  };

  const handleNextWeek = () => {
    const newWeekStart = weekStart.add(navStep, 'day');
    setWeekStart(newWeekStart);
  };

  // Generate days to display
  const days = Array.from({ length: daysToShow }, (_, i) => {
    const day = weekStart.clone().add(i, 'day');
    const monthKey = day.format('YYYYMM');
    const dayKey = day.format('DD');
    const dayData = scheduleData[monthKey]?.days?.[dayKey] || null;
    return {
      date: day,
      data: dayData,
    };
  });

  // Inline CSS styles using Flexbox
  const styles = {
    container: {
      padding: '16px',
      maxWidth: '1600px',
      margin: '0 auto',
    },
    navContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '16px',
    },
    title: {
      fontSize: '20px',
      fontWeight: 500,
    },
    grid: {
      display: 'flex',
      flexWrap: 'nowrap',
      gap: '16px',
      width: '100%',
    },
    dayBoxCard: {
      flex: '1',
      boxSizing: 'border-box',
    },
    dayBoxChild: {
      minHeight: '100px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      backgroundColor: 'white',
    },
    dayContent: {
      fontSize: '12px',
      flex: '1',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
    },
    row: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: 16,
      marginBottom: 8,
      minHeight: 40,
      width: '100%',
    },
    content: {
      display: 'flex',
      width: '100%',
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
    noScans: {
      color: '#6b7280',
      fontSize: '12px',
    },
    loading: {
      fontSize: '14px',
      textAlign: 'center',
    },
  };

  const isAdmin = currentUser.role === 'admin';

  return (
    <div style={styles.container}>
      {/* Navigation Buttons */}
      <div style={styles.navContainer}>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          onClick={handlePreviousWeek}
        >
          Prev
        </Button>
        <h2 style={styles.title}>
          {daysToShow <= 2
            ? `${weekStart.format('MMMM D')} - ${weekStart.add(1, 'day').format('D, YYYY')}`
            : `Week of ${weekStart.format('MMMM D, YYYY')}`}
        </h2>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          onClick={handleNextWeek}
        >
          Next
        </Button>
      </div>

      {/* Day Grid */}
      {loading ? (
        <div style={styles.loading}>Loading...</div>
      ) : (
        <div style={styles.grid}>
          {days.map((day, index) => (
            <TruCard
              key={index}
              title={`${day.date.format('(ddd) MMM D').toUpperCase()}`}
              styles={{ ...styles.dayBoxCard }}
              childStyles={styles.dayBoxChild}
            >
              {/* Day Content */}
              <div style={styles.dayContent}>
                {/* Row 1: Scan Availability */}
                <div style={styles.row}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={!isAdmin}
                    onClick={() => onEditDay(day, 'scans')}
                  >
                    <EditCalendarIcon sx={{ fontSize: 20 }} />
                  </Button>
                  <div style={styles.content}>
                    {day.data && day.data.practitioners?.length > 0 ? (
                      <>
                        <div style={{ fontWeight: 600, fontSize: '0.8em', textTransform: 'uppercase' }}>
                          {day.data.practitioners
                            .map((p) => getScannerLabel(p.id))
                            .filter(Boolean)
                            .join(', ')}
                        </div>
                        <div style={{ fontSize: 13 }}>
                          {day.data.totalScans - day.data.scansBooked} of {day.data.totalScans}
                        </div>
                        <div style={{ fontSize: '0.8em', textTransform: 'uppercase', color: '#6b7280' }}>
                          Available
                        </div>
                      </>
                    ) : (
                      <div style={{ display: 'flex', width: '100%' }}>&nbsp;</div>
                    )}
                  </div>
                </div>
              </div>
            </TruCard>
          ))}
        </div>
      )}
    </div>
  );
};

export default WeekView;